import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  creerDossierPedagogique,
  deposerSupports,
  getEspacePedagogique,
  modifierDossierPedagogique,
  modifierSupport,
  supprimerDossierPedagogique,
  supprimerSupport,
  urlSupport,
} from '../services/pedagogieService';
import { formatDate } from './devisUtils';
import NomEditable from './NomEditable';
import ConfirmationSuppression from '../components/ConfirmationSuppression';
import { toast } from '../components/Toasts';
import '../assets/styles/DemandeDevis.css';
import '../assets/styles/DevisPanel.css';
import '../assets/styles/Devis.css';
import '../assets/styles/Parametrage.css';
import '../assets/styles/EspacePedagogique.css';

/**
 * Espace pédagogique : les supports de formation rangés en dossiers et
 * sous-dossiers.
 *
 * Côté équipe on y crée des dossiers, dépose des fichiers, renomme, déplace et
 * supprime (à la corbeille). Côté formateur (`lectureSeule`) la même page se
 * consulte sans aucun de ces gestes : on navigue, on ouvre, on télécharge.
 */

const FORMATS_SUPPORTS =
  '.pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.odt,.ppt,.pptx,.odp,.xls,.xlsx,.ods,.csv,.txt,.mp4';

function tailleLisible(octets) {
  const n = Number(octets) || 0;
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} Ko`;
  return `${(n / 1024 / 1024).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Mo`;
}

const simplifie = (t) =>
  String(t || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

/** Le type d'un fichier, pour la pastille de sa ligne. */
function sigleDe(mime, nom) {
  const m = String(mime || '');
  if (m === 'application/pdf') return { sigle: 'PDF', classe: 'pdf' };
  if (m.startsWith('image/')) return { sigle: 'IMG', classe: 'image' };
  if (m.startsWith('video/')) return { sigle: 'VID', classe: 'video' };
  if (m.includes('presentation') || m.includes('powerpoint')) return { sigle: 'PPT', classe: 'ppt' };
  if (m.includes('sheet') || m.includes('excel') || m === 'text/csv') return { sigle: 'XLS', classe: 'xls' };
  if (m.includes('word') || m.includes('opendocument.text')) return { sigle: 'DOC', classe: 'doc' };
  const ext = String(nom || '').split('.').pop().toUpperCase().slice(0, 4);
  return { sigle: ext || 'FIC', classe: 'autre' };
}

function IconeDossier() {
  return (
    <svg className="ep-icone ep-icone-dossier" viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
      <path
        d="M3 6.5A2.5 2.5 0 0 1 5.5 4h4l2 2.2h7A2.5 2.5 0 0 1 21 8.7V17.5A2.5 2.5 0 0 1 18.5 20h-13A2.5 2.5 0 0 1 3 17.5V6.5z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Une petite fenêtre à un champ (nouveau dossier). */
function FenetreNom({ titre, libelle, valeurInitiale = '', boutonLibelle, onClose, onValider }) {
  const [nom, setNom] = useState(valeurInitiale);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);

  async function valider(e) {
    e.preventDefault();
    if (!nom.trim()) {
      setErreur('Le nom ne peut pas être vide.');
      return;
    }
    setEnCours(true);
    setErreur(null);
    try {
      await onValider(nom.trim());
    } catch (err) {
      setErreur(err.message || 'Enregistrement impossible.');
      setEnCours(false);
    }
  }

  return (
    <div className="param-modal-overlay" onClick={enCours ? undefined : onClose}>
      <div className="param-modal" style={{ maxWidth: 460, height: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div className="param-modal-header">
          <h2 className="param-modal-title">{titre}</h2>
          <button type="button" className="param-modal-close" onClick={onClose} aria-label="Fermer">
            ×
          </button>
        </div>
        <form className="param-modal-body" onSubmit={valider}>
          <label className="devis-form-field">
            <span>{libelle}</span>
            <input type="text" value={nom} maxLength={150} autoFocus onChange={(e) => setNom(e.target.value)} />
          </label>
          {erreur && <div className="devis-row-erreur">{erreur}</div>}
          <div className="devis-modal-actions" style={{ marginTop: 16 }}>
            <button type="button" className="devis-btn" onClick={onClose} disabled={enCours}>
              Annuler
            </button>
            <button type="submit" className="devis-btn devis-btn-primary" disabled={enCours}>
              {enCours ? 'Enregistrement…' : boutonLibelle}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Choisir où ranger un dossier ou un fichier. */
function FenetreDeplacer({ titre, sousTitre, options, valeurInitiale, onClose, onValider }) {
  const [cible, setCible] = useState(valeurInitiale === null ? '' : String(valeurInitiale));
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);

  async function valider(e) {
    e.preventDefault();
    setEnCours(true);
    setErreur(null);
    try {
      await onValider(cible === '' ? null : Number(cible));
    } catch (err) {
      setErreur(err.message || 'Déplacement impossible.');
      setEnCours(false);
    }
  }

  return (
    <div className="param-modal-overlay" onClick={enCours ? undefined : onClose}>
      <div className="param-modal" style={{ maxWidth: 520, height: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div className="param-modal-header">
          <h2 className="param-modal-title">{titre}</h2>
          <button type="button" className="param-modal-close" onClick={onClose} aria-label="Fermer">
            ×
          </button>
        </div>
        <form className="param-modal-body" onSubmit={valider}>
          <p className="ep-aide">{sousTitre}</p>
          <label className="devis-form-field">
            <span>Ranger dans</span>
            <select value={cible} onChange={(e) => setCible(e.target.value)}>
              <option value="">Espace pédagogique (racine)</option>
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.chemin}
                </option>
              ))}
            </select>
          </label>
          {erreur && <div className="devis-row-erreur">{erreur}</div>}
          <div className="devis-modal-actions" style={{ marginTop: 16 }}>
            <button type="button" className="devis-btn" onClick={onClose} disabled={enCours}>
              Annuler
            </button>
            <button type="submit" className="devis-btn devis-btn-primary" disabled={enCours}>
              {enCours ? 'Déplacement…' : 'Déplacer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EspacePedagogique({ lectureSeule = false }) {
  const [donnees, setDonnees] = useState({ dossiers: [], fichiers: [] });
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [courant, setCourant] = useState(null);
  const [recherche, setRecherche] = useState('');
  const [nouveauDossier, setNouveauDossier] = useState(false);
  const [renommage, setRenommage] = useState(null); // 'd-12' | 'f-7'
  const [deplacement, setDeplacement] = useState(null); // { type, element }
  const [suppression, setSuppression] = useState(null); // { type, element }
  const [occupe, setOccupe] = useState(false);
  const [survol, setSurvol] = useState(false);
  const selecteur = useRef(null);

  const charger = useCallback(() => {
    return getEspacePedagogique()
      .then((res) => {
        setDonnees(res.data || { dossiers: [], fichiers: [] });
        setErreur(null);
      })
      .catch((err) => setErreur(err.message || 'Espace pédagogique indisponible.'))
      .finally(() => setChargement(false));
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);

  const parId = useMemo(() => new Map(donnees.dossiers.map((d) => [d.id, d])), [donnees.dossiers]);

  /** « Parent / Enfant » : le chemin lisible d'un dossier. */
  const cheminDe = useCallback(
    (id) => {
      const noms = [];
      let cur = parId.get(id);
      let garde = 0;
      while (cur && garde < 50) {
        noms.unshift(cur.nom);
        cur = cur.parentId ? parId.get(cur.parentId) : null;
        garde += 1;
      }
      return noms.join(' / ');
    },
    [parId]
  );

  // Si le dossier courant a disparu (supprimé ailleurs), retour à la racine.
  useEffect(() => {
    if (courant !== null && !parId.has(courant)) setCourant(null);
  }, [courant, parId]);

  const filDAriane = useMemo(() => {
    const fil = [];
    let cur = courant !== null ? parId.get(courant) : null;
    let garde = 0;
    while (cur && garde < 50) {
      fil.unshift(cur);
      cur = cur.parentId ? parId.get(cur.parentId) : null;
      garde += 1;
    }
    return fil;
  }, [courant, parId]);

  const recherchee = recherche.trim() !== '';
  const mots = simplifie(recherche).split(/\s+/).filter(Boolean);
  const dossiersAffiches = recherchee
    ? donnees.dossiers.filter((d) => mots.every((m) => simplifie(`${d.nom} ${cheminDe(d.id)}`).includes(m)))
    : donnees.dossiers.filter((d) => d.parentId === courant);
  const fichiersAffiches = recherchee
    ? donnees.fichiers.filter((f) =>
        mots.every((m) =>
          simplifie(`${f.libelle} ${f.nomFichier} ${f.dossierId ? cheminDe(f.dossierId) : ''}`).includes(m)
        )
      )
    : donnees.fichiers.filter((f) => f.dossierId === courant);

  const contenuDe = (id) => ({
    sousDossiers: donnees.dossiers.filter((d) => d.parentId === id).length,
    fichiers: donnees.fichiers.filter((f) => f.dossierId === id).length,
  });

  /** Les dossiers où l'on peut ranger l'élément (jamais dans lui-même ni sa descendance). */
  function optionsDeplacement(type, element) {
    const interdits = new Set();
    if (type === 'dossier') {
      const pile = [element.id];
      while (pile.length) {
        const id = pile.pop();
        interdits.add(id);
        donnees.dossiers.filter((d) => d.parentId === id).forEach((d) => pile.push(d.id));
      }
    }
    return donnees.dossiers
      .filter((d) => !interdits.has(d.id))
      .map((d) => ({ id: d.id, chemin: cheminDe(d.id) }))
      .sort((a, b) => a.chemin.localeCompare(b.chemin, 'fr'));
  }

  async function deposer(fichiers) {
    const liste = Array.from(fichiers || []);
    if (!liste.length || lectureSeule) return;
    setOccupe(true);
    try {
      const res = await deposerSupports(liste, { dossierId: courant });
      const refuses = res.data?.refuses || [];
      if (refuses.length) {
        toast.erreur(`Non déposé : ${refuses.map((r) => `${r.nom} (${r.raison})`).join(' ; ')}`);
      }
      if (res.data?.deposes?.length) toast.succes(res.message);
      await charger();
    } catch (err) {
      toast.erreur(err.message || 'Dépôt impossible.');
    } finally {
      setOccupe(false);
      if (selecteur.current) selecteur.current.value = '';
    }
  }

  async function confirmerSuppression() {
    setOccupe(true);
    try {
      if (suppression.type === 'dossier') await supprimerDossierPedagogique(suppression.element.id);
      else await supprimerSupport(suppression.element.id);
      toast.succes(suppression.type === 'dossier' ? 'Dossier mis à la corbeille.' : 'Fichier mis à la corbeille.');
      setSuppression(null);
      await charger();
    } catch (err) {
      toast.erreur(err.message || 'Suppression impossible.');
      setSuppression(null);
    } finally {
      setOccupe(false);
    }
  }

  const vide = !chargement && dossiersAffiches.length === 0 && fichiersAffiches.length === 0;

  return (
    <div
      className={`devis-page devis-page-scroll${survol ? ' ep-survol' : ''}`}
      onDragOver={
        lectureSeule
          ? undefined
          : (e) => {
              e.preventDefault();
              setSurvol(true);
            }
      }
      onDragLeave={(e) => {
        if (e.currentTarget === e.target) setSurvol(false);
      }}
      onDrop={
        lectureSeule
          ? undefined
          : (e) => {
              e.preventDefault();
              setSurvol(false);
              deposer(e.dataTransfer.files);
            }
      }
    >
      <div className="devis-header">
        <div>
          <h1 className="devis-title">Espace pédagogique</h1>
          <p className="devis-subtitle">
            {lectureSeule
              ? 'Les supports de formation mis à votre disposition par l’équipe.'
              : 'Les supports de formation rangés pour les formateurs : dossiers, sous-dossiers et fichiers. Les formateurs les consultent sans pouvoir les modifier.'}
          </p>
        </div>
        {!lectureSeule && (
          <div className="devis-header-actions">
            <button type="button" className="devis-btn" onClick={() => setNouveauDossier(true)} disabled={occupe}>
              Nouveau dossier
            </button>
            <button
              type="button"
              className="devis-btn devis-btn-primary"
              onClick={() => selecteur.current?.click()}
              disabled={occupe}
            >
              {occupe ? 'Dépôt en cours…' : 'Déposer des fichiers'}
            </button>
            <input
              ref={selecteur}
              type="file"
              multiple
              accept={FORMATS_SUPPORTS}
              className="drive-input-cache"
              aria-label="Choisir des supports à déposer"
              onChange={(e) => deposer(e.target.files)}
            />
          </div>
        )}
      </div>

      <div className="devis-toolbar">
        <input
          type="search"
          className="devis-search"
          placeholder="Rechercher un dossier ou un support…"
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
        />
      </div>

      {!recherchee && (
        <nav className="ep-fil" aria-label="Chemin du dossier">
          <button type="button" className={`ep-fil-lien${courant === null ? ' ep-fil-courant' : ''}`} onClick={() => setCourant(null)}>
            Espace pédagogique
          </button>
          {filDAriane.map((d, i) => (
            <span key={d.id} className="ep-fil-etape">
              <span className="ep-fil-sep" aria-hidden="true">
                ›
              </span>
              <button
                type="button"
                className={`ep-fil-lien${i === filDAriane.length - 1 ? ' ep-fil-courant' : ''}`}
                onClick={() => setCourant(d.id)}
              >
                {d.nom}
              </button>
            </span>
          ))}
        </nav>
      )}
      {recherchee && (
        <div className="ep-aide">
          {dossiersAffiches.length + fichiersAffiches.length} résultat(s) dans tout l’espace.
        </div>
      )}

      {chargement && <div className="devis-state">Chargement de l’espace pédagogique…</div>}
      {erreur && <div className="devis-state devis-state-error">{erreur}</div>}

      {!chargement && !erreur && (
        <div className="devis-card ep-liste">
          {vide && (
            <div className="ep-vide">
              {recherchee
                ? 'Rien ne correspond à cette recherche.'
                : lectureSeule
                  ? 'Aucun support pour l’instant.'
                  : 'Ce dossier est vide. Créez un dossier ou déposez des fichiers (vous pouvez aussi les glisser sur la page).'}
            </div>
          )}

          {dossiersAffiches.map((d) => {
            const c = contenuDe(d.id);
            const cle = `d-${d.id}`;
            return (
              <div className="ep-ligne" key={cle}>
                <div
                  className="ep-ligne-principale"
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setRecherche('');
                    setCourant(d.id);
                  }}
                  onKeyDown={(e) => {
                    // Les touches tapées dans le champ de renommage ne sont pas celles de la ligne.
                    if (e.target !== e.currentTarget) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setRecherche('');
                      setCourant(d.id);
                    }
                  }}
                  title="Ouvrir le dossier"
                >
                  <IconeDossier />
                  <span className="ep-ligne-texte">
                    {renommage === cle ? (
                      <NomEditable
                        valeur={d.nom}
                        edition
                        onRenommer={async (nom) => {
                          await modifierDossierPedagogique(d.id, { nom });
                          await charger();
                        }}
                        onFermer={() => setRenommage(null)}
                      />
                    ) : (
                      <span className="ep-nom">{d.nom}</span>
                    )}
                    <span className="ep-meta">
                      {recherchee && d.parentId ? `${cheminDe(d.parentId)} · ` : ''}
                      {c.sousDossiers} sous-dossier{c.sousDossiers > 1 ? 's' : ''} · {c.fichiers} fichier
                      {c.fichiers > 1 ? 's' : ''}
                    </span>
                  </span>
                </div>
                {!lectureSeule && (
                  <div className="ep-actions">
                    <button type="button" className="drive-action" onClick={() => setRenommage(cle)} disabled={occupe}>
                      Renommer
                    </button>
                    <button
                      type="button"
                      className="drive-action"
                      onClick={() => setDeplacement({ type: 'dossier', element: d })}
                      disabled={occupe}
                    >
                      Déplacer
                    </button>
                    <button
                      type="button"
                      className="drive-action drive-action-danger"
                      onClick={() => setSuppression({ type: 'dossier', element: d })}
                      disabled={occupe}
                    >
                      Supprimer
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {fichiersAffiches.map((f) => {
            const cle = `f-${f.id}`;
            const { sigle, classe } = sigleDe(f.mime, f.nomFichier);
            return (
              <div className="ep-ligne" key={cle}>
                <div
                  className="ep-ligne-principale"
                  role="button"
                  tabIndex={0}
                  onClick={() => window.open(urlSupport(f.id, { apercu: true }), '_blank')}
                  onKeyDown={(e) => {
                    // Les touches tapées dans le champ de renommage ne sont pas celles de la ligne.
                    if (e.target !== e.currentTarget) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      window.open(urlSupport(f.id, { apercu: true }), '_blank');
                    }
                  }}
                  title="Ouvrir"
                >
                  <span className={`ep-sigle ep-sigle-${classe}`}>{sigle}</span>
                  <span className="ep-ligne-texte">
                    {renommage === cle ? (
                      <NomEditable
                        valeur={f.libelle}
                        edition
                        onRenommer={async (nom) => {
                          await modifierSupport(f.id, { libelle: nom });
                          await charger();
                        }}
                        onFermer={() => setRenommage(null)}
                      />
                    ) : (
                      <span className="ep-nom">{f.libelle}</span>
                    )}
                    <span className="ep-meta">
                      {recherchee && f.dossierId ? `${cheminDe(f.dossierId)} · ` : ''}
                      {tailleLisible(f.taille)} · déposé le {formatDate(f.creeLe)}
                    </span>
                  </span>
                </div>
                <div className="ep-actions">
                  <a className="drive-action" href={urlSupport(f.id)} download title="Télécharger">
                    Télécharger
                  </a>
                  {!lectureSeule && (
                    <>
                      <button type="button" className="drive-action" onClick={() => setRenommage(cle)} disabled={occupe}>
                        Renommer
                      </button>
                      <button
                        type="button"
                        className="drive-action"
                        onClick={() => setDeplacement({ type: 'fichier', element: f })}
                        disabled={occupe}
                      >
                        Déplacer
                      </button>
                      <button
                        type="button"
                        className="drive-action drive-action-danger"
                        onClick={() => setSuppression({ type: 'fichier', element: f })}
                        disabled={occupe}
                      >
                        Supprimer
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {nouveauDossier && (
        <FenetreNom
          titre="Nouveau dossier"
          libelle={courant === null ? 'Nom du dossier (à la racine)' : `Nom du dossier (dans « ${cheminDe(courant)} »)`}
          boutonLibelle="Créer le dossier"
          onClose={() => setNouveauDossier(false)}
          onValider={async (nom) => {
            await creerDossierPedagogique({ nom, parentId: courant });
            setNouveauDossier(false);
            await charger();
          }}
        />
      )}

      {deplacement && (
        <FenetreDeplacer
          titre={deplacement.type === 'dossier' ? 'Déplacer le dossier' : 'Déplacer le fichier'}
          sousTitre={deplacement.type === 'dossier' ? deplacement.element.nom : deplacement.element.libelle}
          options={optionsDeplacement(deplacement.type, deplacement.element)}
          valeurInitiale={deplacement.type === 'dossier' ? deplacement.element.parentId : deplacement.element.dossierId}
          onClose={() => setDeplacement(null)}
          onValider={async (cible) => {
            if (deplacement.type === 'dossier') await modifierDossierPedagogique(deplacement.element.id, { parentId: cible });
            else await modifierSupport(deplacement.element.id, { dossierId: cible });
            setDeplacement(null);
            await charger();
          }}
        />
      )}

      {suppression && (
        <ConfirmationSuppression
          titre={suppression.type === 'dossier' ? 'Supprimer ce dossier ?' : 'Supprimer ce fichier ?'}
          sousTitre={suppression.type === 'dossier' ? suppression.element.nom : suppression.element.libelle}
          details={
            suppression.type === 'dossier'
              ? [
                  `Ses ${contenuDe(suppression.element.id).sousDossiers} sous-dossier(s) et ses ${contenuDe(suppression.element.id).fichiers} fichier(s), avec tout leur contenu.`,
                ]
              : ['Le fichier ne sera plus visible des formateurs.']
          }
          note="Les fichiers restent sur le serveur ; l’élément est restaurable depuis la Corbeille."
          enCours={occupe}
          onAnnuler={() => setSuppression(null)}
          onConfirmer={confirmerSuppression}
        />
      )}
    </div>
  );
}

export default EspacePedagogique;
